import { useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Select, DatePicker, Switch, message, Popconfirm
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, GlobalOutlined, NotificationOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { announcementsApi } from '../api/announcements';
import { firmsApi } from '../api/firms';
import type { Announcement, CreateAnnouncementRequest } from '../types';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

export function Announcements() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const isGlobal = Form.useWatch('isGlobal', form);

  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: announcementsApi.getAll,
  });

  const { data: firms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: firmsApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: announcementsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      message.success('Duyuru oluşturuldu');
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error('Duyuru oluşturulamadı'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => announcementsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      message.success('Duyuru güncellendi');
      setIsModalOpen(false);
      setEditingAnnouncement(null);
      form.resetFields();
    },
    onError: () => message.error('Duyuru güncellenemedi'),
  });

  const deleteMutation = useMutation({
    mutationFn: announcementsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      message.success('Duyuru silindi');
    },
    onError: () => message.error('Duyuru silinemedi'),
  });

  const handleSubmit = (values: any) => {
    const data: CreateAnnouncementRequest = {
      title: values.title,
      message: values.message,
      type: values.type,
      startsAt: values.dateRange[0].toISOString(),
      endsAt: values.dateRange[1].toISOString(),
      isGlobal: values.isGlobal,
      firmIds: values.isGlobal ? undefined : values.firmIds,
    };

    if (editingAnnouncement) {
      updateMutation.mutate({
        id: editingAnnouncement.id,
        data: { ...data, isActive: values.isActive }
      });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    form.setFieldsValue({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      dateRange: [dayjs(announcement.startsAt), dayjs(announcement.endsAt)],
      isGlobal: announcement.isGlobal,
      firmIds: announcement.firmIds,
      isActive: announcement.isActive,
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingAnnouncement(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'info',
      isGlobal: true,
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'blue';
      case 'warning': return 'orange';
      case 'success': return 'green';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'info': return 'Bilgi';
      case 'warning': return 'Uyarı';
      case 'success': return 'Başarı';
      case 'error': return 'Hata';
      default: return type;
    }
  };

  const isAnnouncementActive = (announcement: Announcement) => {
    const now = new Date();
    const starts = new Date(announcement.startsAt);
    const ends = new Date(announcement.endsAt);
    return announcement.isActive && now >= starts && now <= ends;
  };

  const columns = [
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: Announcement) => (
        <Space>
          <NotificationOutlined />
          {title}
          {isAnnouncementActive(record) && <Tag color="green">Aktif</Tag>}
        </Space>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{getTypeText(type)}</Tag>
      ),
    },
    {
      title: 'Kapsam',
      key: 'scope',
      render: (_: unknown, record: Announcement) => (
        record.isGlobal ? (
          <Tag icon={<GlobalOutlined />} color="purple">Tüm Firmalar</Tag>
        ) : (
          <Tag>{record.firms.length} Firma</Tag>
        )
      ),
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startsAt',
      key: 'startsAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Bitiş',
      dataIndex: 'endsAt',
      key: 'endsAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Oluşturan',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: Announcement) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Duyuruyu silmek istediğinize emin misiniz?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Duyurular</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Duyuru
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={announcements}
        rowKey="id"
        loading={isLoading}
      />

      <Modal
        title={editingAnnouncement ? 'Duyuru Düzenle' : 'Yeni Duyuru'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingAnnouncement(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="Başlık"
            rules={[{ required: true, message: 'Başlık gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="message"
            label="Mesaj"
            rules={[{ required: true, message: 'Mesaj gerekli' }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item
            name="type"
            label="Tip"
            rules={[{ required: true, message: 'Tip gerekli' }]}
          >
            <Select>
              <Select.Option value="info">
                <Tag color="blue">Bilgi</Tag>
              </Select.Option>
              <Select.Option value="warning">
                <Tag color="orange">Uyarı</Tag>
              </Select.Option>
              <Select.Option value="success">
                <Tag color="green">Başarı</Tag>
              </Select.Option>
              <Select.Option value="error">
                <Tag color="red">Hata</Tag>
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Gösterim Tarihleri"
            rules={[{ required: true, message: 'Tarih aralığı gerekli' }]}
          >
            <RangePicker
              showTime
              format="DD.MM.YYYY HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="isGlobal"
            label="Tüm Firmalara Göster"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {!isGlobal && (
            <Form.Item
              name="firmIds"
              label="Firmalar"
              rules={[{ required: true, message: 'En az bir firma seçin' }]}
            >
              <Select
                mode="multiple"
                placeholder="Firma seçin"
                optionFilterProp="children"
              >
                {firms.map((firm) => (
                  <Select.Option key={firm.id} value={firm.id}>
                    {firm.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {editingAnnouncement && (
            <Form.Item
              name="isActive"
              label="Aktif"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingAnnouncement ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
